// Admin → Import: bring Smartsheet registers into PM C3 (rows, comments, attachments with all versions, images).
// Trial run first (nothing written to the registers), then Import, then the check report.
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useApp } from '../../lib/app';
import { api } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import { useRows } from '../../lib/useRows';
import { fmtBytes, fmtDateTime } from '../../lib/format';
import { Badge, Button, Card, Empty, errText, useToast } from '../../ui/kit';

type Sheet = { key: string; label: string; type: string; ss_sheet_id: string };
type Job = {
  id: string;
  sheet_key: string;
  mode: 'trial' | 'import';
  status: string;
  phase: string | null;
  counts: Record<string, number>;
  report: Record<string, any>;
  error: string | null;
  started_at: string;
  finished_at: string | null;
  heartbeat_at: string | null;
};

const PHASE: Record<string, string> = {
  rows: 'Reading rows',
  discussions: 'Reading comments',
  attachments: 'Listing attachments and versions',
  files: 'Copying files',
  summary: 'Summing up',
  verify: 'Checking',
  done: 'Finished',
};

function Report({ job }: { job: Job }) {
  const r = job.report ?? {};
  const c = job.counts ?? {};
  const a = r.attachments;
  const v = r.verify;
  return (
    <div className="report">
      {r.rows ? (
        <div>
          <strong>{r.rows.count} rows</strong>
          {r.rows.by_status ? ` — ${Object.entries(r.rows.by_status).map(([s, n]) => `${n} ${s.replace('_', ' ')}`).join(', ')}` : ''}
          {r.rows.no_topic ? ` · ${r.rows.no_topic} without a topic` : ''}
        </div>
      ) : null}
      {r.rows?.unmatched_buildings?.length ? (
        <div>
          <strong>Building names not matched</strong> — the items are imported and flagged <em>To be reviewed</em>; fix them in PM C3, or add the name as an “other name” on Buildings and they link themselves:
          <ul>
            {r.rows.unmatched_buildings.map((u: { name: string; rows: number }) => (
              <li key={u.name}>
                {u.name} — {u.rows} row{u.rows > 1 ? 's' : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {r.rows?.unmatched_orgs?.length ? (
        <div>
          <strong>Organisation names not matched</strong> — flagged <em>To be reviewed</em> (or add as “other names” on Organisations):
          <ul>
            {r.rows.unmatched_orgs.map((u: { name: string; rows: number }) => (
              <li key={u.name}>
                {u.name} — {u.rows}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {r.rows?.unreadable?.length ? (
        <div>
          <strong>Values the import could not read</strong> ({r.rows.to_review} rows, flagged <em>To be reviewed</em>):
          <ul>
            {r.rows.unreadable.slice(0, 12).map((u: { number: string | null; field: string; raw: string }, i: number) => (
              <li key={i}>
                {u.number ?? '—'} · {u.field.replace('_', ' ')}: “{u.raw}”
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {c.comments !== undefined ? (
        <div>
          <strong>{c.discussions ?? 0} conversations, {c.comments} comments</strong>
          {c.sheet_discussions ? ` · ${c.sheet_discussions} sheet-level conversation(s) not linked to a row (not imported)` : ''}
          {job.mode === 'import' && c.comments_inserted !== undefined ? ` · ${c.comments_inserted} new comments added` : ''}
        </div>
      ) : null}
      {a ? (
        <div>
          <strong>
            {a.documents} documents, {a.versions_total} file versions ({a.older_versions} older), {a.total_mb} MB
          </strong>
          {a.on_comments ? ` · ${a.on_comments} on comments` : ''}
          {a.sheet_level ? ` · ${a.sheet_level} sheet-level` : ''}
          {Object.keys(a.links ?? {}).length ? ` · links: ${Object.entries(a.links).map(([k, n]) => `${n} ${k.replace('_', ' ').toLowerCase()}`).join(', ')}` : ''}
          {a.over_50mb?.length ? (
            <div className="banner banner-warn">Over 50 MB (may need the storage upload limit raised): {a.over_50mb.map((x: { name: string; mb: number }) => `${x.name} (${x.mb} MB)`).join(', ')}</div>
          ) : null}
        </div>
      ) : null}
      {r.images?.count ? <div>{r.images.count} images in cells</div> : null}
      {job.mode === 'import' && c.items_inserted !== undefined ? (
        <div>
          Items: {c.items_inserted} added, {c.items_updated ?? 0} updated, {c.items_same ?? 0} unchanged{c.items_kept ? `, ${c.items_kept} kept as edited in PM C3` : ''}
          {c.files_done || c.links_done || c.images_done ? ` · copied ${c.files_done ?? 0} files, ${c.images_done ?? 0} images, ${c.links_done ?? 0} links (${fmtBytes(c.bytes_copied ?? 0)})` : ''}
        </div>
      ) : null}
      {v ? (
        <div className={`banner ${v.ok ? 'banner-info' : 'banner-warn'}`}>
          <span>
            <strong>{v.ok ? 'Check passed' : 'Check: needs attention'}</strong> — rows {v.rows_smartsheet} in Smartsheet / {v.items_pm_c3} in PM C3 · comments{' '}
            {v.comments_smartsheet} / {v.comments_pm_c3} · files {v.files_done} copied, {v.files_failed} failed, {v.files_skipped} skipped, {v.files_pending} waiting
          </span>
        </div>
      ) : null}
      {v?.failed?.length ? (
        <ul>
          {v.failed.map((f: { name: string; error: string }, i: number) => (
            <li key={i}>
              {f.name}: {f.error}
            </li>
          ))}
        </ul>
      ) : null}
      {job.error ? <div className="error-box">{job.error}</div> : null}
    </div>
  );
}

export default function Import() {
  const { siteId, site, access } = useApp();
  const toast = useToast();
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const jobs = useRows<Job>(
    () =>
      supabase
        .from('import_jobs')
        .select('id, sheet_key, mode, status, phase, counts, report, error, started_at, finished_at, heartbeat_at')
        .eq('site_id', siteId ?? '00000000-0000-0000-0000-000000000000')
        .order('started_at', { ascending: false })
        .limit(100),
    [siteId],
    ['import_jobs'],
  );

  useEffect(() => {
    if (!access?.super_admin) return;
    api<{ sheets: Sheet[] }>('import', { action: 'sheets' })
      .then((r) => setSheets(r.sheets))
      .catch((e) => toast({ tone: 'error', text: errText(e) }));
  }, [access?.super_admin, toast]);

  if (!access?.super_admin) return <Card><Empty>Only the Super Admin can import from Smartsheet.</Empty></Card>;

  async function act(body: Record<string, unknown>, label: string) {
    setBusy(label);
    try {
      await api('import', { site_id: siteId, ...body });
      toast({ tone: 'ok', text: `${label} started — you can leave this page; it carries on in the background.` });
      void jobs.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="stack">
      <Card>
        <p style={{ margin: 0 }}>
          Imports into <strong>{site?.name}</strong>. Start with a <strong>Trial run</strong>: it reads the sheet, its comments and every attachment
          version and reports what it found — nothing is added to the registers. Then <strong>Import</strong>. Re-running is safe: rows update in place
          and files already copied are skipped. Each import is one batch in the audit log, so it can be undone.
        </p>
      </Card>
      {sheets.map((s) => {
        const mine = jobs.rows.filter((j) => j.sheet_key === s.key);
        const last = mine[0];
        const lastTrial = mine.find((j) => j.mode === 'trial' && j.status === 'done');
        const lastImport = mine.find((j) => j.mode === 'import');
        const running = last && ['queued', 'running'].includes(last.status);
        const stale = running && last.heartbeat_at && Date.now() - new Date(last.heartbeat_at).getTime() > 5 * 60_000;
        const qc = last?.counts ?? {};
        const pct =
          last?.phase === 'files' && last.report?.attachments
            ? Math.min(100, Math.round((100 * ((qc.files_done ?? 0) + (qc.links_done ?? 0) + (qc.images_done ?? 0) + (qc.files_failed ?? 0) + (qc.files_skipped ?? 0))) / Math.max(1, last.report.attachments.versions_total + (last.report.images?.count ?? 0) + Object.values(last.report.attachments.links ?? {}).reduce((a: number, b) => a + Number(b), 0))))
            : null;
        return (
          <Card
            key={s.key}
            title={s.label}
            actions={
              <span className="actions">
                <Link to={`/r/${s.key}`} style={{ color: '#fff' }}>
                  Open register
                </Link>
              </span>
            }
          >
            <div className="stack" style={{ gap: 10 }}>
              <div className="toolbar">
                <Button onClick={() => void act({ action: 'start', sheet_key: s.key, mode: 'trial' }, `Trial run of ${s.label}`)} disabled={Boolean(busy) || Boolean(running && !stale)}>
                  Trial run
                </Button>
                <Button
                  kind="primary"
                  onClick={() => {
                    if (!lastTrial && !window.confirm('No trial run yet for this sheet. Import anyway?')) return;
                    void act({ action: 'start', sheet_key: s.key, mode: 'import' }, `Import of ${s.label}`);
                  }}
                  disabled={Boolean(busy) || Boolean(running && !stale)}
                >
                  {lastImport ? 'Import again (update)' : 'Import'}
                </Button>
                {running ? (
                  <Button kind="danger" onClick={() => void act({ action: 'stop', job_id: last.id }, 'Stop')}>
                    Stop
                  </Button>
                ) : null}
                {last && (stale || last.status === 'failed' || last.status === 'stopped') && last.phase !== 'done' ? (
                  <Button onClick={() => void act({ action: 'resume', job_id: last.id }, 'Resume')}>Resume</Button>
                ) : null}
                {lastImport?.report?.verify?.files_failed ? (
                  <Button onClick={() => void act({ action: 'retry', sheet_key: s.key }, 'Retry of failed files')}>Retry failed files</Button>
                ) : null}
                <span className="muted small">Smartsheet sheet {s.ss_sheet_id}</span>
              </div>
              {last ? (
                <>
                  <div className="item-head small">
                    <Badge tone={last.status === 'done' ? 'good' : last.status === 'failed' ? 'dark' : last.status === 'stopped' ? 'warn' : 'orange'}>
                      {last.mode === 'trial' ? 'Trial' : 'Import'} · {last.status}
                    </Badge>
                    <span className="muted">
                      {PHASE[last.phase ?? ''] ?? last.phase ?? 'Waiting to start'} · started {fmtDateTime(last.started_at)}
                      {last.finished_at ? ` · finished ${fmtDateTime(last.finished_at)}` : ''}
                      {stale ? ' · no progress for 5+ minutes — press Resume' : ''}
                    </span>
                  </div>
                  {pct !== null && running ? (
                    <div className="progress" aria-label={`Copying files ${pct}%`}>
                      <span style={{ width: `${pct}%` }} />
                    </div>
                  ) : null}
                  <Report job={last} />
                </>
              ) : (
                <span className="muted small">Not run yet.</span>
              )}
            </div>
          </Card>
        );
      })}
      {jobs.rows.length ? (
        <Card title="History" pad={false}>
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Sheet</th>
                  <th>Run</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Finished</th>
                </tr>
              </thead>
              <tbody>
                {jobs.rows.map((j) => (
                  <tr key={j.id}>
                    <td>{sheets.find((s) => s.key === j.sheet_key)?.label ?? j.sheet_key}</td>
                    <td>{j.mode}</td>
                    <td>{j.status}</td>
                    <td className="small">{fmtDateTime(j.started_at)}</td>
                    <td className="small">{fmtDateTime(j.finished_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
